import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { portalApi, PortalApiError } from "@/lib/portal-api"
import type {
  PortalClientInfo,
  PortalDocument,
  PortalInvoiceDetail,
  PortalInvoiceSummary,
  PortalUnbilledSummary,
} from "@/lib/database.types"

export function usePortalAuth() {
  const navigate = useNavigate()

  const query = useQuery({
    queryKey: ["portal", "me"],
    queryFn: () => portalApi<PortalClientInfo>("/api/portal/me"),
    retry: false,
  })

  useEffect(() => {
    if (query.error instanceof PortalApiError && query.error.statusCode === 401) {
      navigate("/", { replace: true })
    }
  }, [navigate, query.error])

  return query
}

export function usePortalUnbilled() {
  return useQuery({
    queryKey: ["portal", "unbilled"],
    queryFn: () => portalApi<PortalUnbilledSummary>("/api/portal/unbilled"),
  })
}

export function usePortalInvoices() {
  return useQuery({
    queryKey: ["portal", "invoices"],
    queryFn: () => portalApi<PortalInvoiceSummary[]>("/api/portal/invoices"),
  })
}

export function usePortalInvoice(invoiceId: string) {
  return useQuery({
    queryKey: ["portal", "invoices", invoiceId],
    queryFn: () => portalApi<PortalInvoiceDetail>(`/api/portal/invoices/${invoiceId}`),
    enabled: Boolean(invoiceId),
  })
}

export function usePortalDocuments() {
  return useQuery({
    queryKey: ["portal", "documents"],
    queryFn: () => portalApi<PortalDocument[]>("/api/portal/documents"),
  })
}
